/**
 * Original GDI educational theory diagrams (NZ left-side driving).
 * Decorative for sighted users; accessible alt comes from parent (must not reveal answer).
 */
export default function TheoryVisualDiagram({ kind, alt, enlarge = false }) {
  const size = enlarge ? 280 : 200;
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 120 120",
    role: "img",
    "aria-hidden": alt ? undefined : true,
    "aria-label": alt || undefined,
    focusable: "false",
  };

  let body = null;
  switch (kind) {
    case "stop_sign":
      body = (
        <g>
          <polygon points="60,8 98,30 98,70 60,112 22,70 22,30" fill="#c62828" stroke="#1a1a1a" strokeWidth="2" />
          <text x="60" y="68" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="700" fontFamily="system-ui,sans-serif">
            STOP
          </text>
        </g>
      );
      break;
    case "give_way_sign":
      body = (
        <g>
          <polygon points="60,108 12,20 108,20" fill="#fff" stroke="#c62828" strokeWidth="6" />
          <text x="60" y="52" textAnchor="middle" fill="#1a1a1a" fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif">
            GIVE
          </text>
          <text x="60" y="64" textAnchor="middle" fill="#1a1a1a" fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif">
            WAY
          </text>
        </g>
      );
      break;
    case "t_intersection_give_way":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <rect x="10" y="40" width="100" height="28" fill="#dfe6e9" />
          <rect x="46" y="68" width="28" height="42" fill="#dfe6e9" />
          <line x1="10" y1="54" x2="110" y2="54" strokeDasharray="4 3" stroke="#f1c40f" />
          <circle cx="60" cy="92" r="7" fill="#2980b9" stroke="#1a1a1a" />
          <text x="60" y="95" textAnchor="middle" fill="#fff" fontSize="7" fontFamily="system-ui,sans-serif">
            YOU
          </text>
          <circle cx="28" cy="54" r="6" fill="#c0392b" stroke="#1a1a1a" />
          <circle cx="92" cy="54" r="6" fill="#27ae60" stroke="#1a1a1a" />
          <path d="M60 84 L60 70" stroke="#2980b9" strokeWidth="2" markerEnd="url(#arrow)" />
        </g>
      );
      break;
    case "roundabout_give_way":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <circle cx="60" cy="60" r="38" fill="#dfe6e9" />
          <circle cx="60" cy="60" r="14" fill="#fff" />
          <path d="M60 28 A32 32 0 1 1 59.9 28" stroke="#7f8c8d" strokeDasharray="5 4" />
          <circle cx="60" cy="98" r="7" fill="#2980b9" stroke="#1a1a1a" />
          <text x="60" y="101" textAnchor="middle" fill="#fff" fontSize="6" fontFamily="system-ui,sans-serif">
            YOU
          </text>
          <circle cx="88" cy="48" r="6" fill="#c0392b" stroke="#1a1a1a" />
          <circle cx="40" cy="40" r="6" fill="#27ae60" stroke="#1a1a1a" />
          <path d="M60 90 L60 78" stroke="#2980b9" strokeWidth="2" />
        </g>
      );
      break;
    case "uncontrolled_intersection_right":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <rect x="46" y="10" width="28" height="100" fill="#dfe6e9" />
          <rect x="10" y="46" width="100" height="28" fill="#dfe6e9" />
          <circle cx="60" cy="92" r="7" fill="#2980b9" stroke="#1a1a1a" />
          <text x="60" y="95" textAnchor="middle" fill="#fff" fontSize="6" fontFamily="system-ui,sans-serif">
            YOU
          </text>
          <circle cx="92" cy="60" r="7" fill="#c0392b" stroke="#1a1a1a" />
          <text x="92" y="63" textAnchor="middle" fill="#fff" fontSize="6" fontFamily="system-ui,sans-serif">
            R
          </text>
          <path d="M60 84 L60 70" stroke="#2980b9" strokeWidth="2" />
          <path d="M84 60 L72 60" stroke="#c0392b" strokeWidth="2" />
        </g>
      );
      break;
    case "traffic_light_flashing_amber":
      body = (
        <g>
          <rect x="44" y="16" width="32" height="88" rx="6" fill="#2c3e50" />
          <circle cx="60" cy="34" r="10" fill="#7f8c8d" />
          <circle cx="60" cy="60" r="10" fill="#f1c40f" stroke="#f39c12" strokeWidth="2" />
          <circle cx="60" cy="86" r="10" fill="#7f8c8d" />
          <text x="60" y="114" textAnchor="middle" fill="#333" fontSize="8" fontFamily="system-ui,sans-serif">
            flashing
          </text>
        </g>
      );
      break;
    case "traffic_light_green":
      body = (
        <g>
          <rect x="44" y="16" width="32" height="88" rx="6" fill="#2c3e50" />
          <circle cx="60" cy="34" r="10" fill="#7f8c8d" />
          <circle cx="60" cy="60" r="10" fill="#7f8c8d" />
          <circle cx="60" cy="86" r="10" fill="#27ae60" />
        </g>
      );
      break;
    case "railway_crossing_look_both_ways":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <rect x="10" y="48" width="100" height="24" fill="#dfe6e9" />
          <line x1="20" y1="20" x2="100" y2="20" />
          <line x1="20" y1="28" x2="100" y2="28" />
          <line x1="30" y1="16" x2="30" y2="32" />
          <line x1="50" y1="16" x2="50" y2="32" />
          <line x1="70" y1="16" x2="70" y2="32" />
          <line x1="90" y1="16" x2="90" y2="32" />
          <path d="M40 70 L28 70 M28 70 L34 64 M28 70 L34 76" stroke="#c0392b" />
          <path d="M80 70 L92 70 M92 70 L86 64 M92 70 L86 76" stroke="#c0392b" />
          <circle cx="60" cy="88" r="7" fill="#2980b9" stroke="#1a1a1a" />
          <text x="60" y="91" textAnchor="middle" fill="#fff" fontSize="6" fontFamily="system-ui,sans-serif">
            YOU
          </text>
        </g>
      );
      break;
    case "flush_median_right_turn":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <rect x="8" y="20" width="104" height="80" fill="#dfe6e9" />
          <rect x="52" y="24" width="16" height="72" fill="#f7f1c5" stroke="#f1c40f" strokeDasharray="3 2" />
          <circle cx="36" cy="80" r="7" fill="#2980b9" stroke="#1a1a1a" />
          <path d="M36 72 L36 50 L60 50" stroke="#2980b9" strokeWidth="2" />
          <text x="60" y="46" textAnchor="middle" fill="#333" fontSize="7" fontFamily="system-ui,sans-serif">
            right turn
          </text>
        </g>
      );
      break;
    case "multi_lane_roundabout":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <circle cx="60" cy="60" r="42" fill="#dfe6e9" />
          <circle cx="60" cy="60" r="26" fill="none" stroke="#7f8c8d" strokeDasharray="4 3" />
          <circle cx="60" cy="60" r="12" fill="#8fae7a" />
          <circle cx="60" cy="102" r="7" fill="#2980b9" stroke="#1a1a1a" />
          <text x="60" y="105" textAnchor="middle" fill="#fff" fontSize="6" fontFamily="system-ui,sans-serif">
            YOU
          </text>
          <path d="M60 94 L60 82" stroke="#2980b9" strokeWidth="2" />
          <path d="M46 26c8-6 20-6 28 0" stroke="#c0392b" strokeWidth="2" />
        </g>
      );
      break;
    case "intersection_right_turn_give_way":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <rect x="46" y="10" width="28" height="100" fill="#dfe6e9" />
          <rect x="10" y="46" width="100" height="28" fill="#dfe6e9" />
          <circle cx="60" cy="92" r="7" fill="#2980b9" stroke="#1a1a1a" />
          <text x="60" y="95" textAnchor="middle" fill="#fff" fontSize="6" fontFamily="system-ui,sans-serif">
            YOU
          </text>
          <circle cx="60" cy="28" r="7" fill="#c0392b" stroke="#1a1a1a" />
          <path d="M60 84 L60 72" stroke="#2980b9" strokeWidth="2" />
          <path d="M52 84 L44 78" stroke="#2980b9" strokeWidth="2" />
          <path d="M60 36 L60 48" stroke="#c0392b" strokeWidth="2" />
          <path d="M68 36 L76 42" stroke="#c0392b" strokeWidth="2" />
        </g>
      );
      break;
    case "driveway_give_way":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <rect x="10" y="70" width="100" height="30" fill="#dfe6e9" />
          <line x1="10" y1="85" x2="110" y2="85" strokeDasharray="4 3" stroke="#f1c40f" />
          <rect x="46" y="16" width="28" height="54" fill="#e8e2c8" />
          <circle cx="60" cy="46" r="7" fill="#2980b9" stroke="#1a1a1a" />
          <text x="60" y="49" textAnchor="middle" fill="#fff" fontSize="6" fontFamily="system-ui,sans-serif">
            YOU
          </text>
          <path d="M60 54 L60 66" stroke="#2980b9" strokeWidth="2" />
          <path d="M20 100 L100 100" stroke="#7f8c8d" strokeWidth="3" />
          <circle cx="30" cy="100" r="4" fill="#27ae60" stroke="#1a1a1a" />
        </g>
      );
      break;
    case "traffic_light_red":
      body = (
        <g>
          <rect x="44" y="16" width="32" height="88" rx="6" fill="#2c3e50" />
          <circle cx="60" cy="34" r="10" fill="#e74c3c" stroke="#c0392b" strokeWidth="2" />
          <circle cx="60" cy="60" r="10" fill="#7f8c8d" />
          <circle cx="60" cy="86" r="10" fill="#7f8c8d" />
        </g>
      );
      break;
    case "broken_yellow_edge":
      body = (
        <g>
          <rect x="10" y="10" width="100" height="100" fill="#dfe6e9" />
          <line x1="20" y1="10" x2="20" y2="110" stroke="#f1c40f" strokeWidth="4" strokeDasharray="10 6" />
          <line x1="60" y1="10" x2="60" y2="110" stroke="#fff" strokeWidth="2" strokeDasharray="6 6" />
        </g>
      );
      break;
    case "broken_yellow_centre":
      body = (
        <g>
          <rect x="10" y="10" width="100" height="100" fill="#dfe6e9" />
          <line x1="60" y1="10" x2="60" y2="110" stroke="#f1c40f" strokeWidth="4" strokeDasharray="10 6" />
          <line x1="18" y1="10" x2="18" y2="110" stroke="#fff" strokeWidth="2" />
          <line x1="102" y1="10" x2="102" y2="110" stroke="#fff" strokeWidth="2" />
        </g>
      );
      break;
    case "solid_yellow_centre":
      body = (
        <g>
          <rect x="10" y="10" width="100" height="100" fill="#dfe6e9" />
          <line x1="60" y1="10" x2="60" y2="110" stroke="#f1c40f" strokeWidth="4" />
          <line x1="18" y1="10" x2="18" y2="110" stroke="#fff" strokeWidth="2" />
          <line x1="102" y1="10" x2="102" y2="110" stroke="#fff" strokeWidth="2" />
        </g>
      );
      break;
    case "one_lane_bridge_priority_blue":
      body = (
        <g>
          <rect x="28" y="24" width="64" height="72" rx="4" fill="#1565c0" stroke="#fff" strokeWidth="3" />
          <path d="M60 34 L60 86" stroke="#fff" strokeWidth="5" />
          <path d="M52 42 L60 34 L68 42" fill="#fff" stroke="none" />
          <path d="M52 78 L60 86 L68 78" fill="#e53935" stroke="none" />
          <text x="60" y="108" textAnchor="middle" fill="#333" fontSize="7" fontFamily="system-ui,sans-serif">
            priority (NZ)
          </text>
        </g>
      );
      break;
    case "temporary_accident_zone":
      body = (
        <g>
          <rect x="22" y="30" width="76" height="52" rx="4" fill="#ffeb3b" stroke="#1a1a1a" strokeWidth="2" />
          <text x="60" y="52" textAnchor="middle" fill="#1a1a1a" fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif">
            CRASH
          </text>
          <text x="60" y="68" textAnchor="middle" fill="#c62828" fontSize="14" fontWeight="700" fontFamily="system-ui,sans-serif">
            20
          </text>
        </g>
      );
      break;
    case "open_road_speed_limit":
      body = (
        <g>
          <circle cx="60" cy="58" r="34" fill="#fff" stroke="#1a1a1a" strokeWidth="3" />
          <line x1="38" y1="80" x2="82" y2="36" stroke="#1a1a1a" strokeWidth="4" />
          <text x="60" y="108" textAnchor="middle" fill="#333" fontSize="7" fontFamily="system-ui,sans-serif">
            open road
          </text>
        </g>
      );
      break;
    case "lane_closed_left_diagram":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <rect x="12" y="40" width="96" height="40" fill="#dfe6e9" />
          <line x1="12" y1="60" x2="108" y2="60" stroke="#fff" strokeWidth="2" />
          <line x1="40" y1="40" x2="40" y2="80" stroke="#c62828" strokeWidth="3" />
          <path d="M28 52 L36 60 L28 68" stroke="#c62828" strokeWidth="2" fill="none" />
          <text x="74" y="56" textAnchor="middle" fill="#333" fontSize="7" fontFamily="system-ui,sans-serif">
            merge right
          </text>
        </g>
      );
      break;
    case "advisory_speed_plaque":
      body = (
        <g>
          <polygon points="60,18 88,58 60,98 32,58" fill="#ffeb3b" stroke="#1a1a1a" strokeWidth="2" />
          <rect x="44" y="78" width="32" height="22" rx="2" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
          <text x="60" y="56" textAnchor="middle" fill="#1a1a1a" fontSize="16" fontWeight="700" fontFamily="system-ui,sans-serif">
            !
          </text>
          <text x="60" y="94" textAnchor="middle" fill="#c62828" fontSize="11" fontWeight="700" fontFamily="system-ui,sans-serif">
            45
          </text>
        </g>
      );
      break;
    case "railway_crossing_stop_sign":
      body = (
        <g stroke="#333" fill="none" strokeWidth="2">
          <line x1="18" y1="22" x2="102" y2="22" />
          <line x1="18" y1="30" x2="102" y2="30" />
          <rect x="10" y="52" width="100" height="22" fill="#dfe6e9" />
          <polygon points="60,62 78,72 78,88 60,98 42,88 42,72" fill="#c62828" stroke="#1a1a1a" strokeWidth="1.5" />
          <text x="60" y="86" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="700" fontFamily="system-ui,sans-serif">
            STOP
          </text>
          <circle cx="60" cy="108" r="5" fill="#2980b9" stroke="#1a1a1a" />
        </g>
      );
      break;
    default:
      return null;
  }

  return (
    <div
      className="flex justify-center py-2"
      data-testid="theory-visual"
      data-visual-kind={kind}
    >
      <svg {...common} className="max-w-full h-auto border border-border rounded bg-white">
        {body}
      </svg>
    </div>
  );
}
